import React, { useState } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Breadcrumbs, IconButton, Link, Menu, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBreadcrumbs } from "../../../../context/BreadcrumbsProvider";

const PageHeaderBreadcrumbs = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { breadcrumbs, goToBreadcrumb } = useBreadcrumbs();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	// If there are not breadcrumbs dont render
	if (!breadcrumbs.length) return null;

	// Number of visible items before collapse
	const ITEMS_BEFORE = 2;

	// Number of visible items after collapse
	const ITEMS_AFTER = 1;

	// Determine if collapse is needed
	// Only collapse if total items exceed visible slots
	const shouldCollapse = breadcrumbs.length > ITEMS_BEFORE + ITEMS_AFTER;

	// Split breadcrumbs ONLY if collapse is needed
	const startItems = shouldCollapse ? breadcrumbs.slice(0, ITEMS_BEFORE) : breadcrumbs;

	const endItems = shouldCollapse ? breadcrumbs.slice(-ITEMS_AFTER) : [];

	const hiddenItems = shouldCollapse ? breadcrumbs.slice(ITEMS_BEFORE, breadcrumbs.length - ITEMS_AFTER) : [];

	// Helper function to render a single breadcrumb
	const renderBreadcrumb = (breadcrumb: any, index: number, isLast: boolean) => {
		const Icon = breadcrumb.icon;

		// Use dynamic label if available, otherwise use static label
		const label = breadcrumb.dynamicLabel?.toString() ?? t(`navigation:breadcrumbs/${breadcrumb.label}`);

		// Check if breadcrumb should be clickable
		const isClickable = !!breadcrumb.path && !breadcrumb.isLabelOnly && !isLast;

		// Icon + label
		const content = (
			<>
				{Icon && <Icon style={{ fontSize: 20, marginRight: 6 }} />}
				{label}
			</>
		);

		// Render non-clickable breadcrumb, last item or only label
		if (!isClickable) {
			return (
				<Typography
					key={`${breadcrumb.pageType}-${index}`}
					color={isLast ? "text.primary" : "inherit"}
					display="flex"
					alignItems="center"
				>
					{content}
				</Typography>
			);
		}

		// Render clickable breadcrumb
		return (
			<Link
				key={`${breadcrumb.pageType}-${index}`}
				component="button"
				underline="hover"
				color="inherit"
				display="flex"
				alignItems="center"
				onClick={() => {
					// Update breadcrumb stack (trim to selected)
					goToBreadcrumb(breadcrumb.pageType);

					// Navigate to target route
					if (breadcrumb.path) {
						navigate(breadcrumb.path);
					}
				}}
			>
				{content}
			</Link>
		);
	};

	return (
		<>
			<Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
				{/* START ITEMS always visible */}
				{startItems.map((b, i) => renderBreadcrumb(b, i, !shouldCollapse && i === breadcrumbs.length - 1))}

				{/* COLLAPSE BUTTON only if needed */}
				{shouldCollapse && (
					<IconButton size="small" onClick={handleOpenMenu}>
						<MoreHorizIcon fontSize="small" />
					</IconButton>
				)}

				{/* END ITEMS last items always visible */}
				{shouldCollapse &&
					endItems.map((b, i) => {
						const realIndex = breadcrumbs.length - ITEMS_AFTER + i;

						const isLast = realIndex === breadcrumbs.length - 1;

						return renderBreadcrumb(b, realIndex, isLast);
					})}
			</Breadcrumbs>

			{/* Dropdown menu for hidden breadcrumbs */}
			<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
				{hiddenItems.map((breadcrumb, index) => {
					const label =
						breadcrumb.dynamicLabel?.toString() ?? t(`navigation:breadcrumbs/${breadcrumb.label}`);

					return (
						<MenuItem
							key={`${breadcrumb.pageType}-hidden-${index}`}
							onClick={() => {
								// Update breadcrumb state
								goToBreadcrumb(breadcrumb.pageType);

								// Navigate to selected breadcrumb
								if (breadcrumb.path) {
									navigate(breadcrumb.path);
								}

								// Close menu after click
								handleCloseMenu();
							}}
						>
							{label}
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
};

export default PageHeaderBreadcrumbs;
